import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel,
    FormGroup, Radio, RadioGroup, TextField, Typography,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import QuizIcon from "@mui/icons-material/Quiz";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import type { ParsedForm, ParsedFormSection, ParsedQuestion } from "../../../../hooks/Clients/dashboard/useGetActiveForms";
import { parseActiveFormRows } from "../../../../hooks/Clients/dashboard/useGetActiveForms";
import useUpdateFormAnswers from "../../../../hooks/Clients/dashboard/useUpdateFormAnswers";
import { TabPanel } from "./ui";

interface QuestionnaireTabProps {
    activeTab: number;
    personId: string | number;
    activeForms: ParsedForm[];
    loadingActiveForms: boolean;
    onSaved: () => void;
}

const FORM_FIELD_PATTERN = /^(boolean|numeric|text|date)(0[1-9]|1[0-9]|20)$/;
const NUMERIC_PATTERN = /^-?\d+$/;

const getCheckboxValues = (value: string, optionValues: string[]): string[] => {
    if (!value) return [];
    if (value.includes(",")) return value.split(",").map(v => v.trim()).filter(Boolean);

    return optionValues.filter(optionValue => {
        const normalizedOptionValue = String(optionValue);
        if (!normalizedOptionValue) return false;
        if (normalizedOptionValue === "0") return value === "0";
        return value.includes(normalizedOptionValue);
    });
};

const serializeCheckboxValues = (values: string[]): string => {
    if (values.includes("0")) return "0";
    return values.join("");
};

const buildInitialAnswers = (sections: ParsedFormSection[]): Record<string, string> => {
    const answers: Record<string, string> = {};
    sections.forEach(section => {
        section.questions.forEach(question => {
            const selectedOptions = question.options.filter(option => option.selected);
            if (selectedOptions.length === 0) return;
            if (question.answerType === "checkbox") {
                answers[question.dbFieldName] = serializeCheckboxValues(selectedOptions.map(option => String(option.value)));
                return;
            }
            answers[question.dbFieldName] = String(selectedOptions[0].value ?? selectedOptions[0].answer ?? "");
        });
    });
    return answers;
};

const normalizeFormAnswers = (answers: Record<string, string>): Record<string, string> => Object.fromEntries(
    Object.entries(answers).flatMap(([rawKey, rawValue]) => {
        const key = rawKey.trim().toLowerCase();
        const value = rawValue.trim();
        if (!key || !value) return [];
        if (!FORM_FIELD_PATTERN.test(key)) return [];
        if ((key.startsWith("numeric") || key.startsWith("boolean")) && !NUMERIC_PATTERN.test(value)) return [];
        return [[key, value]];
    })
);

const QuestionnaireTab = ({
    activeTab, personId, activeForms, loadingActiveForms, onSaved,
}: QuestionnaireTabProps) => {
    const { updateFormAnswers, isLoading: savingFormAnswers, error: saveError } = useUpdateFormAnswers();
    const [editing, setEditing] = useState(false);
    const [answers, setAnswers] = useState<Record<string, string>>({});

    const latestForm: ParsedForm | undefined = activeForms.length > 0
        ? [...activeForms].sort((a, b) => {
            const vA = parseFloat(a.version) || 0;
            const vB = parseFloat(b.version) || 0;
            return vB - vA;
        })[0]
        : undefined;

    const sections = useMemo(
        () => latestForm ? parseActiveFormRows(latestForm.rows) : [],
        [latestForm]
    );

    useEffect(() => {
        setAnswers(buildInitialAnswers(sections));
        setEditing(false);
    }, [sections]);

    const handleCancel = () => {
        setAnswers(buildInitialAnswers(sections));
        setEditing(false);
    };

    const handleSave = async () => {
        if (!latestForm) return;
        const normalizedAnswers = normalizeFormAnswers(answers);
        const formPayload: Record<string, string> = { ...normalizedAnswers };
        if (latestForm.answeredFormId) {
            formPayload.answeredFormId = latestForm.answeredFormId;
        }
        const ok = await updateFormAnswers(personId, {
            [`form_${latestForm.formId}`]: formPayload,
        });
        if (ok) {
            toast.success("Questionnaire enregistré.");
            setEditing(false);
            onSaved();
        } else {
            toast.error(saveError || "Erreur lors de l'enregistrement du questionnaire.");
        }
    };

    const handleRadioChange = (dbFieldName: string, value: string) => {
        setAnswers(prev => ({ ...prev, [dbFieldName]: value }));
    };

    const handleCheckboxChange = (question: ParsedQuestion, value: string, checked: boolean) => {
        const sortedOpts = [...question.options].sort((a, b) => a.sortOrder - b.sortOrder);
        const optionValues = sortedOpts.map(opt => String(opt.value));
        setAnswers(prev => {
            const current = getCheckboxValues(prev[question.dbFieldName] ?? "", optionValues);
            const next = checked
                ? value === "0"
                    ? ["0"]
                    : [...new Set([...current.filter(v => v !== "0"), value])]
                : current.filter(v => v !== value);
            const normalizedNext = next.length === 0 && optionValues.includes("0") ? ["0"] : next;
            return { ...prev, [question.dbFieldName]: serializeCheckboxValues(normalizedNext) };
        });
    };

    return (
    <TabPanel value={activeTab} index={3}>
        {loadingActiveForms ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        ) : !latestForm ? (
            <Card variant="outlined">
                <CardContent sx={{ textAlign: "center", py: 6 }}>
                    <QuizIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography color="text.secondary">Aucun questionnaire renseigné</Typography>
                </CardContent>
            </Card>
        ) : (() => {
            return (
                <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                    <Box sx={{
                        px: 2.5, py: 1.5,
                        background: "linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.04) 100%)",
                        borderBottom: "2px solid",
                        borderColor: "primary.100",
                        display: "flex", alignItems: "center", gap: 1.5,
                    }}>
                        <QuizIcon sx={{ color: "primary.main", fontSize: 20 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary.dark">
                                {latestForm.name || "Questionnaire Client"}
                                {latestForm.version ? (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        v{latestForm.version}
                                    </Typography>
                                ) : null}
                            </Typography>
                        </Box>
                        {editing ? (
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={handleCancel} disabled={savingFormAnswers}>
                                    Annuler
                                </Button>
                                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={savingFormAnswers}>
                                    Enregistrer
                                </Button>
                            </Box>
                        ) : (
                            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                                Modifier
                            </Button>
                        )}
                    </Box>

                    <Box sx={{
                        p: 2.5,
                        columnWidth: "260px",
                        columnGap: "20px",
                    }}>
                        {sections.map((section, si) => (
                            <Box
                                key={section.parentId}
                                sx={{
                                    breakInside: "avoid",
                                    WebkitColumnBreakInside: "avoid",
                                    mb: 2.5,
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1.5,
                                }}
                            >
                                {section.questions.map(q => {
                                    const sortedOpts = [...q.options].sort((a, b) => a.sortOrder - b.sortOrder);
                                    const optionValues = sortedOpts.map(opt => String(opt.value));
                                    const selectedValue = editing
                                        ? answers[q.dbFieldName] ?? ""
                                        : q.options.find(o => o.selected)?.value ?? "";
                                    const selectedValues = editing ? getCheckboxValues(answers[q.dbFieldName] ?? "", optionValues) : [];
                                    return (
                                        <Box key={q.questionId} sx={{ mb: 2.5 }}>
                                            <Typography
                                                variant="body2" fontWeight={700} color="text.primary"
                                                sx={{ mb: 0.75, fontSize: "0.82rem", borderLeft: "3px solid", borderColor: "primary.light", pl: 1, lineHeight: 1.4 }}
                                            >
                                                {q.question}
                                            </Typography>
                                            {q.answerType === "radio" && (
                                                <RadioGroup value={selectedValue} onChange={(event) => handleRadioChange(q.dbFieldName, event.target.value)}>
                                                    {sortedOpts.map((opt, i) => (
                                                        <FormControlLabel key={i} value={opt.value}
                                                            control={<Radio size="small" disabled={!editing} sx={{ py: 0.2, "&.Mui-disabled": { color: opt.selected ? "primary.main" : "action.disabled" } }} />}
                                                            label={<Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: String(selectedValue) === String(opt.value) ? 700 : 400, color: String(selectedValue) === String(opt.value) ? "primary.dark" : "text.secondary" }}>{opt.answer}</Typography>}
                                                            sx={{ ml: 0, mr: 0 }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            )}
                                            {q.answerType === "checkbox" && (
                                                <FormGroup>
                                                    {sortedOpts.map((opt, i) => (
                                                        <FormControlLabel key={i}
                                                            control={<Checkbox size="small" checked={editing ? selectedValues.includes(String(opt.value)) : opt.selected} disabled={!editing} onChange={(event) => handleCheckboxChange(q, String(opt.value), event.target.checked)} sx={{ py: 0.2, "&.Mui-disabled": { color: opt.selected ? "primary.main" : "action.disabled" } }} />}
                                                            label={<Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: (editing ? selectedValues.includes(String(opt.value)) : opt.selected) ? 700 : 400, color: (editing ? selectedValues.includes(String(opt.value)) : opt.selected) ? "primary.dark" : "text.secondary" }}>{opt.answer}</Typography>}
                                                            sx={{ ml: 0, mr: 0 }}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            )}
                                            {(q.answerType === "date_month_year" || q.answerType === "date") && (
                                                editing ? (
                                                    <TextField
                                                        size="small"
                                                        type="date"
                                                        value={answers[q.dbFieldName] ?? ""}
                                                        onChange={(event) => setAnswers(prev => ({ ...prev, [q.dbFieldName]: event.target.value }))}
                                                        inputProps={{ style: { fontSize: "0.82rem" } }}
                                                    />
                                                ) : (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pl: 1 }}>
                                                        <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                        <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 500, color: selectedValue ? "text.primary" : "text.disabled" }}>
                                                            {q.options.find(o => o.selected)?.value || "—"}
                                                        </Typography>
                                                    </Box>
                                                )
                                            )}
                                            {q.answerType !== "radio" && q.answerType !== "checkbox" && q.answerType !== "date_month_year" && q.answerType !== "date" && (
                                                editing ? (
                                                    <TextField
                                                        size="small"
                                                        value={answers[q.dbFieldName] ?? ""}
                                                        onChange={(event) => setAnswers(prev => ({ ...prev, [q.dbFieldName]: event.target.value }))}
                                                        inputProps={{ style: { fontSize: "0.82rem" } }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" sx={{ fontSize: "0.82rem", pl: 1, color: "text.primary" }}>
                                                        {q.options.filter(o => o.selected).map(o => o.value || o.answer).filter(Boolean).join(", ") || "—"}
                                                    </Typography>
                                                )
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </Card>
            );
        })()}
    </TabPanel>
    );
};

export default QuestionnaireTab;
